import {
  styled,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIconMUI,
  Add as AddIconMUI,
} from '@mui/icons-material';

export const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

export const ContentWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  paddingTop: '2px',
  paddingBottom: 16,
});

export const ItemsListWrapper = styled(Box)({
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  paddingRight: '12px',
  gap: '12px',
});

export const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const TitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '30px',
  paddingLeft: '24px',
  marginBottom: '-2px',
});

export const HeaderColumn = styled(Box)({
  flex: '1 1 250px',
  minWidth: '200px',
});

export const HeaderSpacer = styled(Box)({
  width: '1px',
});

export const HeaderColumnText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#555555',
});

export const ItemBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'itemCount',
})<{ itemCount?: number }>(({ itemCount = 3 }) => ({
  border: '1px solid #D9F1FF',
  borderRadius: '8px',
  padding: 20,
  paddingTop: 10,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '8px',
}));

export const ItemsScrollContainer = styled(Box)({
  maxHeight: 'calc(100vh - 320px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  paddingRight: '8px',
  paddingBottom: '20px',
  scrollbarWidth: 'thin',
  scrollbarColor: '#c1c1c1 #f1f1f1',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
});

export const ItemRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const FieldColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: '1 1 250px',
  minWidth: '200px',
  position: 'relative',
});

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  width: '100%',
  height: '40px',
  backgroundColor: '#FCFEFF',
  borderRadius: '8px',
  '& .MuiSelect-select': {
    padding: '10px 16px',
    fontFamily: 'Outfit',
    fontSize: '14px',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#FF0000' : '#D9F1FF',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F52BA',
  },
  '& .MuiSvgIcon-root': {
    color: '#555555',
  },
}));

export const StyledAutocomplete = styled(Autocomplete)({
  width: '100%',
  '& .MuiAutocomplete-listbox': {
    maxHeight: '350px',
    overflow: 'auto',
  },
}) as typeof Autocomplete;

export const StyledAutocompleteTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '14px',
    fontFamily: 'Outfit',
    borderRadius: '8px',
    backgroundColor: '#FCFEFF',
    '& fieldset': {
      borderColor: hasError ? '#FF0000' : hasValue ? '#0099FF' : '#D9F1FF',
    },
    '&:hover fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0 14px !important',
    fontSize: '14px',
    fontFamily: 'Outfit',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
    '&::placeholder': {
      color: '#75838A',
      opacity: 1,
    },
  },
}));

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    backgroundColor: '#FCFEFF',
    borderRadius: '8px',
    fontFamily: 'Outfit',
    fontSize: '14px',
    '& fieldset': {
      borderColor: hasError ? '#FF0000' : '#D9F1FF',
    },
    '&:hover fieldset': {
      borderColor: '#0F52BA',
    },
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
}));

export const ErrorText = styled(Typography)({
  color: '#FF0000',
  fontSize: '12px',
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 2,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

export const DeleteButton = styled(IconButton)({
  alignSelf: 'flex-end',
  marginBottom: '8px',
  width: '24px',
  height: '24px',
});

export const StyledDeleteIcon = styled(DeleteIconMUI)({
  color: '#75838A',
  fontSize: '16px',
});

export const AddItemButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  position: 'sticky',
  bottom: 0,
  backgroundColor: '#FFFFFF',
  paddingTop: '8px',
  paddingBottom: '8px',
  zIndex: 1,
});

export const AddItemButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: '#0F52BA',
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  padding: 0,
  minWidth: 'auto',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

export const StyledAddIcon = styled(AddIconMUI)({
  fontSize: '24px',
});

export const FooterBox = styled(Box)({
  flexShrink: 0,
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
});

export const BackButton = styled(Button)({
  width: '82px',
  height: '40px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px #E2E3E1',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '16px',
  color: '#0B2330',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

export const NextButton = styled(Button)({
  width: '82px',
  height: '40px',
  backgroundColor: '#0F52BA',
  border: '1px solid #0D4AA9',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px #E2E3E1',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '16px',
  color: '#FFFFFF',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0D4AA9',
  },
});
